import React, { useState, useMemo, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { addDays } from 'date-fns';
import { toZonedTime, fromZonedTime } from 'date-fns-tz';
import { useSchedule } from '../../context/ScheduleContext';
import { toKendoItem, fromKendoItem, blankKendoItem } from '../../utils/kendoAdapter';
import type { KendoItem } from '../../utils/kendoAdapter';
import { CustomEditForm } from './CustomEditForm';
import { formatTimeLabel, resolveTimezone } from '../../utils/dateUtils';
import type { ScheduleEvent, Resource, TimeFormat } from '../../types';

// ─── Helpers ───────────────────────────────────────────────────────────────

const HTML_TAG_RE = /<[a-z][\s\S]*>/i;

function localDateKey(utcDate: Date, tz: string): string {
  const w = toZonedTime(utcDate, tz);
  return `${w.getFullYear()}-${w.getMonth()}-${w.getDate()}`;
}

function fmtTime(utcString: string, tz: string, fmt: TimeFormat): string {
  const w = toZonedTime(new Date(utcString), tz);
  return formatTimeLabel(w.getHours(), w.getMinutes(), fmt);
}

// ─── AgendaView ────────────────────────────────────────────────────────────

export function AgendaView() {
  const {
    config,
    events,
    currentDate,
    handleSaveEvent,
    handleCreateEvent,
    callbacks,
    emit,
    resourcePage,
    setResourcePage,
    holidayEvents,
    pendingNewEventRequest,
    setPendingNewEventRequest,
  } = useSchedule();

  const {
    timezone,
    observeDaylightSaving,
    agendaDays = 30,
    agendaGroupFormat,
    resources,
    resourceGrouping,
    resourcesPerPage: resourcesPerPageConfig,
    allowEventCreation = true,
    timeFormat = '12h',
    timeRange,
    slotDuration,
    eventIcon,
  } = config;

  const groupDateFormat: Intl.DateTimeFormatOptions = agendaGroupFormat ?? {
    weekday: 'short', month: 'short', day: 'numeric', year: 'numeric',
  };

  const resolvedTimezone = resolveTimezone(timezone, observeDaylightSaving);

  const [editingItem, setEditingItem] = useState<KendoItem | null>(null);
  const [isNewEvent, setIsNewEvent] = useState(false);

  // Resource jump picker
  const [showResourcePicker, setShowResourcePicker] = useState(false);
  const [pickerAnchor, setPickerAnchor] = useState<DOMRect | null>(null);
  const resourcePickerBtnRef = useRef<HTMLButtonElement>(null);
  const resourcePickerPopupRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!showResourcePicker) return;
    const handler = (e: MouseEvent) => {
      if (
        resourcePickerBtnRef.current?.contains(e.target as Node) ||
        resourcePickerPopupRef.current?.contains(e.target as Node)
      ) return;
      setShowResourcePicker(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showResourcePicker]);

  // AI-initiated create: open the form when the AI returns a createEvent request
  useEffect(() => {
    if (!pendingNewEventRequest) return;
    const { date, startTime, resourceId, durationMinutes } = pendingNewEventRequest;
    const [y, m, d] = date.split('-').map(Number);
    const [h, min] = startTime.split(':').map(Number);
    const wallStart = new Date(y, m - 1, d, h, min, 0, 0);
    const duration = durationMinutes ?? (slotDuration ?? 30);
    setEditingItem({
      ...blankKendoItem(wallStart, duration),
      resourceId,
      ...(pendingNewEventRequest.title !== undefined && { title: pendingNewEventRequest.title }),
      ...(pendingNewEventRequest.customAttributes?.length && { customAttributes: pendingNewEventRequest.customAttributes }),
    });
    setIsNewEvent(true);
    setPendingNewEventRequest(null);
  }, [pendingNewEventRequest, slotDuration, setPendingNewEventRequest]);

  // Visible resources — mirrors the pagination logic in KendoSchedulerView
  const resourcesPerPage =
    typeof resourcesPerPageConfig === 'number'
      ? resourcesPerPageConfig
      : (resourcesPerPageConfig?.[config.view] ?? 2);

  const paginationActive =
    resourceGrouping !== 'byResource' &&
    (resources?.length ?? 0) > resourcesPerPage;

  const totalResourcePages = paginationActive
    ? Math.ceil((resources?.length ?? 0) / resourcesPerPage)
    : 1;

  const visibleResources = paginationActive
    ? resources?.slice(resourcePage * resourcesPerPage, (resourcePage + 1) * resourcesPerPage)
    : resources;

  const resourceMap = useMemo(() => {
    const map = new Map<string, Resource>();
    (resources ?? []).forEach((r) => map.set(r.id, r));
    return map;
  }, [resources]);

  // Days in the agenda window
  const days = useMemo(
    () => Array.from({ length: agendaDays }, (_, i) => addDays(currentDate, i)),
    [currentDate, agendaDays],
  );

  // Filter to visible resources only; holidays (no resourceId) always pass through.
  // Queued events are excluded — they have no start/end time and belong in the scheduling queue panel.
  // When hideUnassignedEvents is true, non-holiday events with no resourceId are excluded.
  const filteredEvents = useMemo(() => {
    const allEvents = [...events.filter((e) => !e.queued), ...holidayEvents];
    const hideUnassigned = config.hideUnassignedEvents && !!visibleResources?.length;
    if (!visibleResources?.length) {
      return hideUnassigned
        ? allEvents.filter((e) => !!(e.resourceIds?.length || e.resourceId) || String(e.id).startsWith('__holiday__'))
        : allEvents;
    }
    const ids = new Set(visibleResources.map((r) => r.id));
    return allEvents.filter((e) => {
      const eIds = e.resourceIds?.length ? e.resourceIds : (e.resourceId ? [e.resourceId] : []);
      if (eIds.some((id) => ids.has(id))) return true;
      if (eIds.length === 0) return !hideUnassigned || String(e.id).startsWith('__holiday__');
      return false;
    });
  }, [events, holidayEvents, visibleResources, config.hideUnassignedEvents]);

  // Group filtered events by local date key; sort all-day first then by start
  const eventsByDayKey = useMemo(() => {
    const map = new Map<string, ScheduleEvent[]>();
    for (const e of filteredEvents) {
      const key = localDateKey(new Date(e.startUtc), resolvedTimezone);
      const arr = map.get(key) ?? [];
      arr.push(e);
      map.set(key, arr);
    }
    for (const arr of map.values()) {
      arr.sort((a, b) => {
        if (a.isAllDay && !b.isAllDay) return -1;
        if (!a.isAllDay && b.isAllDay) return 1;
        return new Date(a.startUtc).getTime() - new Date(b.startUtc).getTime();
      });
    }
    return map;
  }, [filteredEvents, resolvedTimezone]);

  // Only keep days that actually have events
  const daysWithEvents = useMemo(
    () => days.filter((d) => eventsByDayKey.has(localDateKey(d, resolvedTimezone))),
    [days, eventsByDayKey, resolvedTimezone],
  );

  // ── Handlers ──────────────────────────────────────────────────────────────

  const handleEventClick = (event: ScheduleEvent) => {
    if (event.editable === false) return;
    setEditingItem(toKendoItem(event, resolvedTimezone));
    setIsNewEvent(false);
  };

  const handleSlotClick = (day: Date) => {
    if (!allowEventCreation) return;
    // Build a wall-clock start at the day's configured start time
    const wallDay = toZonedTime(day, resolvedTimezone);
    const [sh, sm] = timeRange.start.split(':').map(Number);
    const wallStart = new Date(
      wallDay.getFullYear(), wallDay.getMonth(), wallDay.getDate(), sh, sm, 0, 0,
    );
    const newItem = blankKendoItem(wallStart, slotDuration);
    setEditingItem(newItem);
    setIsNewEvent(true);

    const startUtc = fromZonedTime(wallStart, resolvedTimezone).toISOString();
    callbacks.onSlotClick?.({ dateUtc: startUtc });
    emit({ type: 'SLOT_CLICKED', payload: { dateUtc: startUtc } });
  };

  const handleFormClose = () => setEditingItem(null);

  const handleFormSubmit = (updatedItem: KendoItem) => {
    const event = fromKendoItem(updatedItem, resolvedTimezone);
    if (isNewEvent) {
      const { id: _id, ...rest } = event;
      handleCreateEvent(rest);
    } else {
      handleSaveEvent(event);
    }
    setEditingItem(null);
  };

  // ── Pagination button style ───────────────────────────────────────────────

  const pagBtnStyle = (disabled: boolean): React.CSSProperties => ({
    padding: '2px 10px',
    fontSize: 13,
    fontWeight: 600,
    background: 'transparent',
    border: '1px solid #cbd5e1',
    borderRadius: 4,
    cursor: disabled ? 'default' : 'pointer',
    color: disabled ? '#cbd5e1' : '#475569',
    lineHeight: 1.5,
  });

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', background: '#fff', color: '#1e293b' }}>
      {/* Resource pagination — identical UI to KendoSchedulerView */}
      {paginationActive && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '4px 12px',
          borderBottom: '1px solid #e2e8f0',
          background: '#f8fafc',
          flexShrink: 0,
          gap: 8,
        }}>
          <button
            style={pagBtnStyle(resourcePage === 0)}
            disabled={resourcePage === 0}
            onClick={() => setResourcePage((p) => Math.max(0, p - 1))}
            aria-label="Previous resources"
          >
            ‹ Prev
          </button>
          <button
            ref={resourcePickerBtnRef}
            style={{
              fontSize: 12,
              color: '#475569',
              background: showResourcePicker ? '#eff6ff' : 'transparent',
              border: `1px solid ${showResourcePicker ? '#bfdbfe' : '#e2e8f0'}`,
              borderRadius: 4,
              padding: '4px 10px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 5,
              minHeight: 32,
              maxWidth: 'min(300px, 50vw)',
              overflow: 'hidden',
            }}
            onClick={() => {
              if (!showResourcePicker && resourcePickerBtnRef.current) {
                setPickerAnchor(resourcePickerBtnRef.current.getBoundingClientRect());
              }
              setShowResourcePicker((v) => !v);
            }}
            aria-haspopup="listbox"
            aria-expanded={showResourcePicker}
            aria-label={`Showing: ${visibleResources?.map((r) => r.name).join(', ')}. Click to jump to a resource.`}
          >
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
              {visibleResources?.map((r) => r.name).join(' · ')}
            </span>
            <span style={{ color: '#94a3b8', flexShrink: 0, whiteSpace: 'nowrap' }}>
              {resourcePage + 1}/{totalResourcePages}
            </span>
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" aria-hidden="true" style={{ flexShrink: 0, opacity: 0.5 }}>
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </button>
          <button
            style={pagBtnStyle(resourcePage >= totalResourcePages - 1)}
            disabled={resourcePage >= totalResourcePages - 1}
            onClick={() => setResourcePage((p) => Math.min(totalResourcePages - 1, p + 1))}
            aria-label="Next resources"
          >
            Next ›
          </button>
        </div>
      )}

      {/* Agenda body */}
      <div style={{ flex: 1, overflowY: 'auto', background: '#fff' }}>
        {daysWithEvents.length === 0 ? (
          <div style={{
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#94a3b8',
            fontSize: 14,
          }}>
            No events scheduled in this period
          </div>
        ) : (
          daysWithEvents.map((day) => {
            const key = localDateKey(day, resolvedTimezone);
            const dayEvents = eventsByDayKey.get(key) ?? [];
            const dayLabel = day.toLocaleDateString('en-US', {
              ...groupDateFormat,
              timeZone: resolvedTimezone,
            });

            return (
              <div key={key}>
                {/* Day header */}
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '6px 16px',
                  background: '#f1f5f9',
                  borderTop: '1px solid #e2e8f0',
                  borderBottom: '1px solid #e2e8f0',
                  position: 'sticky',
                  top: 0,
                  zIndex: 1,
                }}>
                  <span style={{ fontSize: 13, fontWeight: 600, color: '#334155' }}>
                    {dayLabel}
                  </span>
                  {allowEventCreation && (
                    <button
                      onClick={() => handleSlotClick(day)}
                      style={{
                        fontSize: 11,
                        color: '#3B82F6',
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        padding: '2px 6px',
                        borderRadius: 4,
                        fontWeight: 500,
                        lineHeight: 1.4,
                      }}
                      title="Add event on this day"
                      aria-label={`Add event on ${dayLabel}`}
                    >
                      + Add
                    </button>
                  )}
                </div>

                {/* Event rows */}
                {dayEvents.map((event) => {
                  const resource = event.resourceId ? resourceMap.get(event.resourceId) : undefined;
                  const color = event.backgroundColor ?? event.color ?? resource?.color ?? '#3B82F6';
                  const editable = event.editable !== false;
                  const hasHtml = HTML_TAG_RE.test(event.title);

                  // Build time label
                  let timeStr: string;
                  if (event.isAllDay) {
                    timeStr = 'All day';
                  } else {
                    const startLocal = toZonedTime(new Date(event.startUtc), resolvedTimezone);
                    const endLocal = toZonedTime(new Date(event.endUtc), resolvedTimezone);
                    const startStr = fmtTime(event.startUtc, resolvedTimezone, timeFormat);
                    const endStr = fmtTime(event.endUtc, resolvedTimezone, timeFormat);
                    const sameDay =
                      startLocal.getFullYear() === endLocal.getFullYear() &&
                      startLocal.getMonth() === endLocal.getMonth() &&
                      startLocal.getDate() === endLocal.getDate();
                    if (sameDay) {
                      timeStr = `${startStr} – ${endStr}`;
                    } else {
                      const endDateStr = endLocal.toLocaleDateString('en-US', {
                        month: 'short', day: 'numeric', timeZone: resolvedTimezone,
                      });
                      timeStr = `${startStr} – ${endDateStr} ${endStr}`;
                    }
                  }

                  const iconNode = eventIcon ? eventIcon(event) : null;

                  return (
                    <div
                      key={event.id}
                      onClick={editable ? () => handleEventClick(event) : undefined}
                      style={{
                        display: 'grid',
                        gridTemplateColumns: '148px 1fr',
                        gap: 12,
                        padding: '8px 16px',
                        borderBottom: '1px solid #f1f5f9',
                        cursor: editable ? 'pointer' : 'default',
                        alignItems: 'center',
                      }}
                      onMouseEnter={(e) => {
                        if (editable) (e.currentTarget as HTMLElement).style.background = '#f8fafc';
                      }}
                      onMouseLeave={(e) => {
                        (e.currentTarget as HTMLElement).style.background = 'transparent';
                      }}
                    >
                      {/* Time column */}
                      <span style={{ fontSize: 12, color: '#64748b', whiteSpace: 'nowrap' }}>
                        {timeStr}
                      </span>

                      {/* Event details */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                        <span style={{
                          display: 'inline-block',
                          width: 10,
                          height: 10,
                          borderRadius: '50%',
                          background: color,
                          flexShrink: 0,
                        }} />
                        {hasHtml ? (
                          // eslint-disable-next-line react/no-danger
                          <span
                            dangerouslySetInnerHTML={{ __html: event.title }}
                            style={{
                              fontSize: 13,
                              color: '#1e293b',
                              fontWeight: 500,
                              flex: 1,
                              minWidth: 0,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          />
                        ) : (
                          <span style={{
                            fontSize: 13,
                            color: '#1e293b',
                            fontWeight: 500,
                            flex: 1,
                            minWidth: 0,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}>
                            {event.title}
                          </span>
                        )}
                        {resource && (
                          <span style={{ fontSize: 11, color: '#94a3b8', whiteSpace: 'nowrap', flexShrink: 0 }}>
                            {resource.name}
                          </span>
                        )}
                        {iconNode && (
                          <span style={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                            {iconNode}
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })
        )}
      </div>

      {/* Edit / create form */}
      {editingItem && (
        <CustomEditForm
          dataItem={editingItem}
          isNew={isNewEvent}
          onClose={handleFormClose}
          onSubmit={handleFormSubmit}
        />
      )}

      {showResourcePicker && pickerAnchor && createPortal(
        <div
          ref={resourcePickerPopupRef}
          role="listbox"
          aria-label="Jump to resource"
          style={{
            position: 'fixed',
            top: pickerAnchor.bottom + 4,
            left: Math.max(8, Math.min(pickerAnchor.left, window.innerWidth - Math.min(260, window.innerWidth - 16) - 8)),
            width: Math.min(260, window.innerWidth - 16),
            maxHeight: '60vh',
            overflowY: 'auto',
            background: '#fff',
            border: '1px solid #e2e8f0',
            borderRadius: 8,
            boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
            zIndex: 99999,
          }}
        >
          <div style={{
            padding: '8px 14px 6px',
            fontSize: 11,
            fontWeight: 600,
            color: '#94a3b8',
            letterSpacing: '0.05em',
            textTransform: 'uppercase',
            borderBottom: '1px solid #f1f5f9',
            position: 'sticky',
            top: 0,
            background: '#fff',
          }}>
            Jump to Resource
          </div>
          {resources?.map((r, idx) => {
            const page = Math.floor(idx / resourcesPerPage);
            const isCurrentPage = page === resourcePage;
            return (
              <button
                key={r.id}
                role="option"
                aria-selected={isCurrentPage}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  width: '100%',
                  textAlign: 'left',
                  padding: '10px 14px',
                  fontSize: 13,
                  background: isCurrentPage ? '#eff6ff' : 'transparent',
                  color: isCurrentPage ? '#2563eb' : '#1e293b',
                  fontWeight: isCurrentPage ? 600 : 400,
                  border: 'none',
                  borderBottom: '1px solid #f8fafc',
                  cursor: 'pointer',
                  minHeight: 44,
                  boxSizing: 'border-box',
                }}
                onMouseEnter={(e) => { if (!isCurrentPage) (e.currentTarget as HTMLButtonElement).style.background = '#f8fafc'; }}
                onMouseLeave={(e) => { if (!isCurrentPage) (e.currentTarget as HTMLButtonElement).style.background = 'transparent'; }}
                onClick={() => {
                  setResourcePage(page);
                  setShowResourcePicker(false);
                }}
              >
                {r.color && (
                  <span style={{ width: 8, height: 8, borderRadius: '50%', background: r.color, flexShrink: 0 }} />
                )}
                <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {r.name}
                </span>
                {isCurrentPage && (
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ flexShrink: 0, color: '#2563eb' }}>
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                )}
                <span style={{
                  fontSize: 10,
                  color: isCurrentPage ? '#93c5fd' : '#94a3b8',
                  background: isCurrentPage ? '#dbeafe' : '#f1f5f9',
                  borderRadius: 10,
                  padding: '1px 6px',
                  flexShrink: 0,
                }}>
                  p{page + 1}
                </span>
              </button>
            );
          })}
        </div>,
        document.body,
      )}
    </div>
  );
}
